/**
 * `/login` — email + password against `POST /api/auth/sign-in/email`.
 *
 * Design §2.2 also lists magic link and "Sign in with Google/Microsoft". Neither
 * is configured on the server's better-auth instance yet (`packages/auth` wires
 * `organization`, `jwt`, `apiKey` and `bearer` — no social providers, no
 * `magicLink`), so those buttons are shown disabled with the reason instead of
 * being rendered as if they worked.
 *
 * Password sign-in itself is a seam: `createAuth` only enables
 * `emailAndPassword` when its caller passes it, and `apps/api` does not today.
 * The server's answer is surfaced verbatim rather than rewritten into something
 * friendlier — "Sign-in failed" over a 501 would send someone hunting for a typo
 * in their password.
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Field, fieldAria, Input } from '@crm/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { signInWithPassword } from '../api/auth.ts';
import { UnsupportedControl } from '../components/not-wired.tsx';
import { parseLoginSearch } from '../lib/search.ts';
import { zodFormResolver } from '../lib/zod-form.ts';

const loginSchema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});

type LoginValues = z.output<typeof loginSchema>;

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: parseLoginSearch,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues, unknown, LoginValues>({
    resolver: zodFormResolver<LoginValues, LoginValues>(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const submit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await signInWithPassword(values);
      await navigate({ to: redirect ?? '/today' });
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'Sign-in failed');
    }
  });

  return (
    <div className="flex h-full items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Field id="email" label="Email" error={errors.email?.message}>
              <Input
                {...register('email')}
                {...fieldAria('email', { error: errors.email?.message })}
                type="email"
                autoComplete="email"
                autoFocus
              />
            </Field>

            <Field id="password" label="Password" error={errors.password?.message}>
              <Input
                {...register('password')}
                {...fieldAria('password', { error: errors.password?.message })}
                type="password"
                autoComplete="current-password"
              />
            </Field>

            {serverError === null ? null : (
              <p role="alert" className="text-sm font-medium text-destructive">
                {serverError}
              </p>
            )}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <div className="flex flex-wrap gap-1.5 pt-2">
              <UnsupportedControl label="Magic link" procedure="better-auth magicLink plugin" />
              <UnsupportedControl label="Google" procedure="better-auth socialProviders" />
              <UnsupportedControl label="Microsoft" procedure="better-auth socialProviders" />
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
